/**
 * What has happened to one file, under the file itself.
 *
 * This was two tabs next to the file list — an Imports tab and a Number checks
 * tab — which meant answering "what happened to this file" by scanning two
 * other lists for its name. A file's history belongs to the file.
 */
import { Badge, Group, Loader, Stack, Table, Text, Button, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useSheetHistory } from '../api/queries';
import { downloadSheet, downloadVerifyResult } from '../api/upload';
import { formatDateTime, formatNumber } from '../theme';
import { useVocab } from '../vocab';
import { ImportJobCard, importStatusColour, importStatusLabel } from './ImportJobCard';

function StatusBadge({ status }: { status: string }) {
  const colour =
    status === 'completed' ? 'chartGreen' : status === 'failed' ? 'chartRed' : 'chartAmber';
  return <Badge size="xs" variant="light" color={colour}>{status}</Badge>;
}

function Count({ value }: { value: number | null | undefined }) {
  return <>{value == null ? '—' : formatNumber(value)}</>;
}

const NEEDS_A_CARD = new Set(['pending', 'processing', 'paused']);

/** A heading with nothing under it reads as broken, so say what is missing. */
function Section({ title, empty, children }: { title: string; empty?: string; children?: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>{title}</Text>
      {empty ? <Text size="sm" c="dimmed">{empty}</Text> : children}
    </div>
  );
}

export function SheetHistoryPanel({
  sheetId,
  fileName,
}: {
  sheetId: string;
  fileName: string;
}) {
  const words = useVocab();
  const history = useSheetHistory(sheetId);
  /* Which version is downloading, so only its own button shows a spinner. */
  const [downloading, setDownloading] = useState<number | null>(null);

  async function downloadVersion(version: number, isCurrent: boolean) {
    setDownloading(version);
    try {
      await downloadSheet(sheetId, fileName, isCurrent ? undefined : version);
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Download failed',
        message: (e as Error).message,
      });
    }
    setDownloading(null);
  }

  async function download(jobId: string, clean: boolean) {
    try {
      await downloadVerifyResult(jobId, clean);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Download failed', message: (e as Error).message });
    }
  }

  if (history.isPending) {
    return <Group justify="center" py="md"><Loader size="xs" /></Group>;
  }
  const d = history.data;

  return (
    <Stack gap="md" px="md" py="sm">
      {/* Every change now makes a new file, so a file has one version. Files
          changed before that can still hold several; they stay viewable and
          downloadable, but there is no going back to one in place. */}
      {(d?.versions ?? []).length > 1 && (
      <Section
        title="Earlier versions (from before every change made a new file)"
      >
        <Stack gap={4}>
          {(d?.versions ?? []).map((v) => (
            <Group key={v.version} gap="sm">
              <Badge size="xs" variant={v.is_current ? 'filled' : 'light'} color="gray">
                v{v.version}
              </Badge>
              <Text size="sm">
                {/* Nobody made the first one; it is how the file arrived. */}
                {/* What was done comes first, because it is the thing you
                    are looking for. Who and when are context; "Removed
                    2,000,000 rows found in dnc.csv" is the answer. */}
                {v.action || (v.created_by ? 'saved' : 'as uploaded')}
              </Text>
              {v.row_count != null && (
                <Text size="xs" c="dimmed">{formatNumber(v.row_count)} rows</Text>
              )}
              {v.created_by && (
                <Text size="xs" c="dimmed">by {v.created_by}</Text>
              )}
              {v.parent_version != null && (
                <Text size="xs" c="dimmed">from v{v.parent_version}</Text>
              )}
              <Text size="xs" c="dimmed">{formatDateTime(v.created_at)}</Text>
              {v.is_current && <Text size="xs" c="dimmed">· current</Text>}
              {!v.is_current && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  component={Link}
                  to={`/sheets/${sheetId}?version=${v.version}`}
                >
                  Open
                </Button>
              )}
              <Tooltip
                label={
                  v.is_current
                    ? 'Download as CSV, with any unsaved changes applied'
                    : 'Download this version as it was saved'
                }
              >
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  loading={downloading === v.version}
                  leftSection={<IconDownload size={12} />}
                  onClick={() => downloadVersion(v.version, v.is_current)}
                >
                  CSV
                </Button>
              </Tooltip>
            </Group>
          ))}
        </Stack>
      </Section>
      )}

      <Section
        title="Imported into"
        empty={(d?.imports ?? []).length === 0 ? `Never imported into ${words.a('campaign')}.` : undefined}
      >
        {/* Anything still running or stopped gets its controls and live counts;
            the finished ones are the short table below. */}
        <Stack gap="xs" mb={(d?.imports ?? []).some((j) => NEEDS_A_CARD.has(j.status)) ? 'xs' : 0}>
          {(d?.imports ?? [])
            .filter((j) => NEEDS_A_CARD.has(j.status))
            .map((j) => <ImportJobCard key={j.job_id} jobId={j.job_id} title={j.campaign_name || undefined} />)}
        </Stack>
        <Table>
          <Table.Tbody>
            {(d?.imports ?? []).map((j) => (
              <Table.Tr key={j.job_id}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{j.campaign_name || '—'}</Text>
                    {j.sheet_version != null && (
                      <Badge size="xs" variant="light" color="gray">v{j.sheet_version}</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td w={100}>
                  <Badge size="xs" variant="light" color={importStatusColour(j.status)}>
                    {importStatusLabel(j.status, false)}
                  </Badge>
                </Table.Td>
                <Table.Td w={150}>
                  <Text size="xs" c="dimmed">
                    <Count value={j.rows_imported} /> imported
                    {j.rows_invalid ? `, ${formatNumber(j.rows_invalid)} invalid` : ''}
                  </Text>
                </Table.Td>
                <Table.Td w={160}>
                  <Text size="xs" c="dimmed">{formatDateTime(j.created_at)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

      <Section
        title="Number checks"
        empty={(d?.checks ?? []).length === 0 ? 'No numbers checked.' : undefined}
      >
        <Table>
          <Table.Tbody>
            {(d?.checks ?? []).map((j) => (
              <Table.Tr key={j.job_id}>
                <Table.Td w={140}>
                  <Group gap={6} wrap="nowrap">
                    <StatusBadge status={j.status} />
                    {j.sheet_version != null && (
                      <Badge size="xs" variant="light" color="gray">v{j.sheet_version}</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    <Count value={j.rows_processed} /> checked ·{' '}
                    <Count value={j.rows_verified} /> reachable ·{' '}
                    <Count value={j.rows_invalid} /> not
                  </Text>
                </Table.Td>
                <Table.Td w={160}>
                  <Text size="xs" c="dimmed">{formatDateTime(j.created_at)}</Text>
                </Table.Td>
                <Table.Td w={150}>
                  {j.status === 'completed' && (
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Only the numbers the networks confirmed">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconDownload size={12} />}
                          onClick={() => download(j.job_id, true)}
                        >
                          Reachable
                        </Button>
                      </Tooltip>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => download(j.job_id, false)}
                      >
                        All
                      </Button>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

    </Stack>
  );
}
